import { useCallback, useEffect, useRef, useState } from 'react';

// Hook de transcripción local (vista previa) usando Web Speech API
// No interfiere con WebRTC; sirve solo para mostrar texto en tiempo real en el input
export function useLocalSTT(language = 'es') {
    const recognitionRef = useRef(null);
    const [isSupported, setIsSupported] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [previewTranscript, setPreviewTranscript] = useState('');
    // Buffer persistente con el texto confirmado por el motor de STT
    const baseTranscriptRef = useRef('');

    // Mapear idioma a códigos válidos
    const resolveLang = useCallback(() => {
        if (!language) return 'es-ES';
        if (language.startsWith('es')) return 'es-ES';
        if (language.startsWith('en')) return 'en-US';
        if (language.startsWith('pt')) return 'pt-BR';
        if (language.startsWith('fr')) return 'fr-FR';
        return 'es-ES';
    }, [language]);

    // Inicializar soporte
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            setIsSupported(true);
        } else {
            setIsSupported(false);
        }
    }, []);

    const start = useCallback(() => {
        if (isActive) return;
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) return;

        try {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = resolveLang();

            recognition.onresult = (event) => {
                let interim = '';
                // Recorremos a partir del índice sugerido para no reprocesar todo
                const startIndex = event.resultIndex || 0;
                for (let i = startIndex; i < event.results.length; i++) {
                    const res = event.results[i];
                    if (res.isFinal) {
                        // Consolidar al buffer base y añadir un espacio de separación
                        baseTranscriptRef.current = (
                            `${baseTranscriptRef.current}${res[0].transcript} `
                        ).replace(/\s+/g, ' ');
                    } else {
                        interim += res[0].transcript;
                    }
                }
                const preview = `${baseTranscriptRef.current}${interim}`.trim();
                setPreviewTranscript(preview);
            };

            recognition.onerror = (event) => {
                // 'not-allowed' / 'service-not-allowed' = permiso denegado:
                // lo logueamos como error porque bloquea el modo hands-free
                // y el usuario tiene que reactivar el mic desde la barra del
                // browser. Los demás (no-speech, aborted) son ruido normal.
                if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                    console.error('Permiso de micrófono denegado:', event.error);
                } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
                    console.warn('Web Speech API error:', event.error);
                }
            };

            recognition.onend = () => {
                // El reconocimiento puede terminar por silencio prolongado
                // o por límite del browser. Marcamos inactivo y dejamos al
                // caller decidir si reanudar (el effect de hands-free lo
                // hará si las condiciones siguen cumpliéndose).
                setIsActive(false);
            };

            recognition.start();
            recognitionRef.current = recognition;
            setIsActive(true);
        } catch (_) {}
    }, [isActive, resolveLang]);

    const stop = useCallback(() => {
        try {
            if (recognitionRef.current) {
                recognitionRef.current.onresult = null;
                recognitionRef.current.onend = null;
                recognitionRef.current.onerror = null;
                recognitionRef.current.stop();
                recognitionRef.current = null;
            }
        } catch (_) {}
        setIsActive(false);
        setPreviewTranscript('');
        baseTranscriptRef.current = '';
    }, []);

    const reset = useCallback(() => {
        // Limpia buffers y preview sin detener explícitamente
        setPreviewTranscript('');
        baseTranscriptRef.current = '';
    }, []);

    // Limpiar al desmontar
    useEffect(() => {
        return () => stop();
    }, [stop]);

    return {
        isSupported,
        isActive,
        previewTranscript,
        start,
        stop,
        reset
    };
}
